import React, { useMemo, useState } from 'react';
import { CombatResult } from '../types';

export type UIEvent = {
  id: number;
  tick: number;
  type: 'depart' | 'arrival' | 'combat_start' | 'combat_end' | 'combat_log' | 'politics' | 'construction' | 'world';
  text: string;
  systemId?: number;
  x?: number;
  y?: number;
};

type IntelDockProps = {
  events: UIEvent[];
  minimized: boolean;
  onToggleMinimized: () => void;
  onEventClick: (ev: UIEvent) => void;
  myPlayerId?: string | null;
  playerNamesById?: Record<string, string>;
};

export default function IntelDock({ events, minimized, onToggleMinimized, onEventClick, myPlayerId, playerNamesById }: IntelDockProps) {
  const [filter, setFilter] = useState<'ALL' | 'MOVEMENT' | 'COMBAT' | 'CONSTRUCTION' | 'POLITICS' | 'WORLD'>('ALL');
  const [expandedBattle, setExpandedBattle] = useState<number | null>(null);
  const nameOf = (owner?: string) => {
    if (!owner) return '—';
    if (myPlayerId && owner === myPlayerId) return 'You';
    if (owner === 'NPC' || owner === 'Unclaimed') return owner;
    const friendly = playerNamesById?.[owner];
    if (friendly) return friendly;
    return owner.length > 6 ? `${owner.slice(0, 6)}…` : owner;
  };

  const counts = useMemo(() => {
    const movement = events.filter(e => e.type === 'depart' || e.type === 'arrival').length;
    const combat = events.filter(e => e.type === 'combat_start' || e.type === 'combat_end' || e.type === 'combat_log').length;
    const construction = events.filter(e => e.type === 'construction').length;
    const politics = events.filter(e => e.type === 'politics').length;
    const world = events.filter(e => e.type === 'world').length;
    return { all: events.length, movement, combat, construction, politics, world } as const;
  }, [events]);

  const filteredEvents = useMemo(() => {
    if (filter === 'ALL') return events;
    if (filter === 'MOVEMENT') return events.filter(e => e.type === 'depart' || e.type === 'arrival');
    if (filter === 'COMBAT') return events.filter(e => e.type === 'combat_start' || e.type === 'combat_end' || e.type === 'combat_log');
    // Construction not yet emitted; keep placeholder
    if (filter === 'CONSTRUCTION') return events.filter(e => e.type === 'construction');
    if (filter === 'POLITICS') return events.filter(e => e.type === 'politics');
    if (filter === 'WORLD') return events.filter(e => e.type === 'world');
    return events;
  }, [events, filter]);

  // Combat details are now viewed in Battle Viewer; Intel Dock only shows the event list

  return (
    <div className={`intel-dock ${minimized ? 'minimized' : ''}`}>
      <div className="intel-header">
        <div className="intel-title">Intel</div>
        <div className="intel-filters">
          {(['ALL','MOVEMENT','COMBAT','CONSTRUCTION','POLITICS','WORLD'] as const).map(key => {
            const n = key === 'ALL' ? counts.all
              : key === 'MOVEMENT' ? counts.movement
              : key === 'COMBAT' ? counts.combat
              : key === 'CONSTRUCTION' ? counts.construction
              : key === 'POLITICS' ? counts.politics
              : counts.world;
            return (
              <button key={key} className={`chip ${filter === key ? 'active' : ''}`} onClick={() => setFilter(key)}>
                {key}{n ? ` (${n})` : ''}
              </button>
            );
          })}
        </div>
        <button className="minimize-btn" onClick={onToggleMinimized}>{minimized ? '➡' : '⬅'}</button>
      </div>

      {!minimized && (
        <div className="intel-content">
          <div className="events-column">
            {filteredEvents.length === 0 && <div className="empty">No events</div>}
            {filteredEvents.map(ev => (
              <div key={ev.id} className={`event-row type-${ev.type}`} onClick={() => onEventClick(ev)}>
                <span className="icon">
                  {ev.type === 'depart' && '🚀'}
                  {ev.type === 'arrival' && '🛬'}
                  {ev.type === 'combat_start' && '⚔️'}
                  {ev.type === 'combat_end' && '🏁'}
                  {ev.type === 'combat_log' && '📜'}
                  {ev.type === 'politics' && '🏛️'}
                  {ev.type === 'construction' && '🏗️'}
                  {ev.type === 'world' && '🌌'}
                </span>
                <span className="msg">{ev.text}</span>
                <span className="t">t{ev.tick}</span>
              </div>
            ))}
          </div>
          {/* Removed combat details column; use Battle Viewer for deep dives */}
        </div>
      )}
    </div>
  );
}


